// Module 07: Story Presence: Voice, Body, Delivery
// The Art of Story - Learning Module
// How you tell your story matters as much as what you tell

import { CRISIS_HELP } from '../crisisSupport.js';

export const storyModule07Content = {
  id: 'story-presence',
  title: 'Story Presence: Voice, Body, Delivery',
  subtitle: 'How you tell your story matters as much as what you tell',
  description: 'Master the embodied delivery of storytelling, using voice, body language, pacing, and presence to amplify the stories you\'ve crafted, whilst managing nervous habits with compassion and building genuine confidence.',
  duration: '38 minutes',
  image: '/images/mod-07-delivery.png',

  content: {
    overview: `Picture two people telling nearly identical stories at the same event, a panel discussion about career transitions.

**First speaker:** "I left corporate law after fifteen years to start a non-profit. It was terrifying. I questioned everything. But I learnt I'm more resilient than I thought, and now I'm doing work that actually matters."

Good content. Vulnerability. Clear narrative arc. But she delivered it staring at her notes, speaking in a monotone, fidgeting with her pen. Her voice said the words, but her body said "I want this to be over." The audience clapped politely. No one asked questions.

**Second speaker:** Same basic story. Left stable career. Started something new. Terrified. Found resilience. But watch the delivery:

He made eye contact with different people in the audience. When he said "I was terrified," his voice dropped and slowed. You could hear the fear. When he said "I questioned everything," he paused, letting that land. His hands gestured naturally. Not performed, just alive. And when he said "now I'm doing work that actually matters," his whole face lit up.

The audience was riveted. People lined up afterwards to talk to him.

**Same content. Completely different impact.**

You can craft a brilliant story (sensory details, eudaemonic meaning, perfect emotional arc, appropriately calibrated vulnerability) and still have it fall flat if your delivery undermines it.

You've spent six modules learning **what** to say. Now let's talk about **how you deliver it**.

Because when your words and your delivery disagree about how you feel, listeners tend to go with the delivery. Albert Mehrabian's 1967 experiments found this for tone of voice and facial expression. You're saying "This mattered to me," but your body is saying "I'm uncomfortable and want this to be over."

**This module is about alignment:** getting your voice, body, and presence to support the story you're telling, not undermine it.

The good news: presence isn't about being a polished performer. In her book *Presence* (2015), social psychologist Amy Cuddy describes presence as being able to **comfortably express your true thoughts, feelings and values**. Not performing confidence. Not hiding nervousness. Just being genuinely present with your story and your listener.

This module teaches you:
- How to use your voice (tone, pacing, pausing) to create emotional impact
- How to use your body (stance, gestures, eye contact) to build connection
- How to manage nervous habits with compassion (not shame)
- How to rehearse without becoming robotic
- How to be present rather than perfect

Because the stories you've crafted deserve to be told with your full presence. Let's learn how.`,

    learningObjectives: [
      'Understand what Mehrabian\'s, Ekman\'s and Cuddy\'s research does and doesn\'t show about nonverbal communication, and why keeping your delivery in step with your words helps listeners believe you',
      'Master voice dynamics, using tone, pacing, pausing, and volume strategically to create emotional impact and guide listener attention',
      'Develop body language awareness and learn to use stance, gestures, and eye contact to support (not distract from) your story',
      'Recognise and manage common nervous habits (filler words, fidgeting, rushing) with self-compassion rather than shame, using practice to build genuine confidence',
      'Apply Amy Cuddy\'s idea of presence to show up authentically rather than performing perfection, fully engaged with your story and listener in the moment',
    ],

    sections: [
      {
        id: 'nonverbal-foundation',
        title: 'The Foundation: Why Body Language Matters',
        duration: '8 minutes',
        content: {
          intro: `Say someone challenges you on this: "If the story is good enough, delivery shouldn't matter. Content is what counts."

Imagine answering with two videos. Same person, same story, filmed twice.

In the first video, she's telling a powerful story about surviving a car accident, but she's looking down, speaking in a monotone, arms crossed defensively. The content is there, but it feels... flat. Distant.

In the second video, same story. But this time she's making eye contact with the camera. When she describes the moment of impact, her voice drops and slows. When she describes the stranger who helped her, her face softens. Her hands gesture naturally. Alive, not performed.

"Same story," you say. "Which one moved you?"

"The second one. Obviously."

"Why?"

They pause. "I don't know. I could... feel it more. Like I was there with her."

"That's delivery. That's why body language matters."

Before we dive into specific techniques, let's look at what the research on delivery does and doesn't show.`,

          mainContent: `**Albert Mehrabian's Research: The Nonverbal Impact**

You've probably heard the statistic: "7% of communication is words, 38% is tone of voice, 55% is body language."

It's a common misreading. **Mehrabian's research was specifically about communicating feelings and attitudes, not all communication.**

When you're explaining how to assemble furniture, words matter most. But when you're **telling a story about something that mattered to you** (which is inherently about feelings and attitudes), nonverbal communication carries enormous weight.

[RESEARCH: The Mehrabian Myth and Reality | Albert Mehrabian's two small 1967 studies asked listeners to judge a speaker's attitude from single words such as "maybe" or "dear" when the word, the tone of voice and a photographed face disagreed. Listeners went mainly by tone and face. The 7-38-55 split comes from combining those studies, and Mehrabian says it applies only when someone is talking about feelings or attitudes. It has been misread as "words don't matter." They do. But because storytelling is so often about feelings, how you say something affects whether people believe it.]

**What this means for storytelling:**

When your words say **"This was terrifying"** but your voice is flat and your body is closed off, listeners don't fully feel the terror. They process the information, but they don't *experience* it with you.

When your words say **"I was joyful"** but you're speaking in a monotone whilst staring at the floor, listeners don't feel the joy.

**Delivery amplifies or undermines content.**

---

**Paul Ekman: What Microexpressions Can and Can't Tell Us**

Psychologist Paul Ekman spent decades mapping how emotion shows in the face. Building on Haggard and Isaacs, who first described them in 1966, he proposed (with Friesen, from 1969) that **microexpressions**, flashes lasting a fraction of a second, can leak feelings a person is trying to hide.

**Two things are worth separating here, because the popular version of this has run well ahead of the evidence.**

The more solid part: felt emotion often shows on the face, though less consistently than the popular version suggests. A 2019 review by Barrett and colleagues found people smile when happy or scowl when angry more often than chance, with wide variation across people, cultures and situations.

The oversold part: the idea that observers can reliably *read* those flashes. Charles Bond and Bella DePaulo's 2006 meta-analysis pooled 206 documents and 24,483 judges trying to tell truth from lies, and found an average accuracy of **54%**: a coin toss with a slight lean. Training in spotting microexpressions has a thin record too: in a 2019 test by Jordan and colleagues, people trained with the Micro-Expressions Training Tool were no better at detecting lies than untrained people. So nobody in your audience is decoding your face frame by frame.

**Why it still matters for storytelling:**

Because the useful direction is the one that survives the caveat. Listeners are poor lie-detectors, though Bond and DePaulo found they did a little better judging what they heard than what they saw. In our experience, what listeners pick up is the overall fit between your words, face, timing and voice across a whole story, not a single flicker. You can't manage all four consciously while also telling the story.

Which is a piece of good news dressed as a warning: the reliable route to looking like you mean it is meaning it. Tell stories that actually matter to you, and the alignment happens by itself.

[INSIGHT: The Authenticity Signal | Be careful with the strong version of this claim. "People can spot a fake in a heartbeat" is not what the deception studies found (Bond and DePaulo, 2006), and they are fairly brutal about how bad we all are at it. What listeners do seem to register, in our experience, is cruder and slower: a story where the emotion and the delivery don't sit together, noticed as a vague sense that something is off rather than as a caught tell. That would explain why performing vulnerability for effect (Module 6) tends to fall flat. And it points the same way as the good news: you don't have to manufacture authenticity or manage your face. Be present with the real emotion of the story and the alignment looks after itself.]

**The takeaway: You can't perform presence. But you can remove the barriers to it.**

---

**Amy Cuddy: Presence Is Authentic, Not Polished**

Social psychologist Amy Cuddy offers a different way to think about confidence in her 2015 book *Presence*.

**Old definition of confidence:** Polished, perfect, no nervousness
**Cuddy's definition of presence:** being attuned to, and able to comfortably express, your true thoughts, feelings, values and potential

**What the research suggests:** in replications, an open, expansive posture has produced a small rise in how powerful people say they feel, though researchers still debate whether that is more than people guessing what the study expects (Jonas and colleagues, 2017).

When people hold an open, expansive "power pose" for two minutes before a high-stakes situation, they tend to report **feeling slightly more powerful**. The evidence that it lowers anxiety is weak: a 2022 meta-analysis by Elkjær and colleagues found slumped postures made people feel worse, while expansive ones did little compared with standing normally.

An important caveat, because you may have heard the stronger version: the original 2010 study also reported that power posing lowered cortisol and raised testosterone. That hormonal finding **did not replicate**. In 2016 Dana Carney, the study's first author, wrote: "I do not believe that 'power pose' effects are real." She also said the original feelings-of-power measure was picked from several questions after the results came in. What larger replications have found is a small rise in how powerful people say they feel, and nothing measurable in the bloodstream. Treat it as a way to settle yourself before you begin, not as proven physiology.

**Applied to storytelling:**

**Before you tell an important story** (interview, presentation, difficult conversation), spend two minutes in an expansive posture:
- Stand tall
- Hands on hips or arms stretched wide
- Open chest
- Take up space

This isn't about performing dominance. It's **a way of giving yourself a moment to settle and stand tall before you begin**.

[RESEARCH: Embodied Cognition and Confidence | The 2010 power-posing study by Carney, Cuddy and Yap became one of the best-known casualties of psychology's replication crisis: a 2015 replication by Ranehill and colleagues, with 200 participants, recovered the self-reported feelings of power but found no effect on hormones or risk-taking, and Dana Carney, the original first author, publicly withdrew her support for the effect in 2016. What survived is narrower: a small rise in how powerful people say they feel. A 2022 meta-analysis of 73 studies by Elkjær and colleagues found the real difference came from slumped, closed postures making people feel and act worse; expansive postures did little compared with a neutral one. The practical lesson is mostly: don't collapse in on yourself before you speak. Before telling a vulnerable story, you might notice yourself closing up ("This is risky, close up, protect yourself"). An open posture for two minutes (the 2010 study used two one-minute poses) can help you feel more ready ("I can be open, I can take up space"). Treat it as a way of settling yourself, not as physiological regulation.]

**The practice: Presence preparation before important storytelling moments.**

---

**The Alignment Principle**

The core principle of this module:

**Your voice, body, and words need to tell the same story.**

When they align, you create:
- **Trust** (listeners believe you)
- **Connection** (they feel with you)
- **Impact** (the story lands)

When they misalign, you create:
- **Confusion** (mixed messages)
- **Distance** (something feels off)
- **Diminished impact** (the story doesn't land)

**The work of this module: Learning to notice and align your delivery with your content.**`,

          practicalApplication: `**Body-Mind Alignment Check**

Before your next important conversation or presentation:

**Step 1: Notice your current state**
- How am I standing/sitting? (Collapsed? Tense? Open?)
- What's my breath like? (Shallow? Held? Deep?)
- What's my internal narrative? ("I'm nervous" / "I've got this" / "I hope they like me")

**Step 2: Spend 2 minutes in an expansive posture**
- Stand with feet hip-width apart
- Hands on hips or arms stretched wide
- Chest open, shoulders back
- Breathe deeply
- (Do this in private: bathroom, car, empty room)

**Step 3: Notice the shift**
- Has your breathing changed?
- Do you feel more grounded?
- Has your internal narrative shifted?

This isn't about becoming a different person. It's about **noticing how you're holding yourself and choosing to open up**.

Try this before telling a story and notice if your delivery feels different.`,
        },
      },

      {
        id: 'voice-dynamics',
        title: 'Voice: Tone, Pacing, Pausing, Volume',
        duration: '9 minutes',
        content: {
          intro: `Picture recording yourself telling a story and being horrified.

Not because you stumbled over words or forgot parts. The content was there. But your voice. Good grief, your voice. Completely flat. Monotone. Like reading a weather report, not sharing something that actually mattered to you.

"Do I really sound like that?" you ask a friend.

"Yeah," she says gently. "You kind of do. It's like... you're trying so hard not to be dramatic that you've flattened all the life out of it."

She's right. You were so afraid of "performing" that you'd stripped all the natural vocal variation out. The result? Stories that should have connected just... didn't.

Your voice is the primary instrument of storytelling. And many of us use only a narrow slice of our vocal range in everyday conversation, then wonder why our stories fall flat.

Let's learn to use your voice with more range and intention. Not performing. Just... alive.`,

          mainContent: `**The Four Elements of Voice**

**1. TONE (The Emotional Colour)**

When your tone and your words disagree about a feeling, listeners tend to believe the tone.

**Example: Say "I'm fine" in three different tones:**
- Flat, monotone → Listener thinks: "You're not fine"
- Bright, energised → Listener thinks: "You're actually fine"
- Sarcastic → Listener thinks: "You're definitely not fine and you're annoyed I asked"

Same words. Completely different meaning.

**In storytelling:**

When you're describing an emotional moment, let your tone reflect it:

❌ **Flat tone:** "I was terrified when I heard the news." (Sounds like reading a grocery list)

✅ **Tonal shift:** "I was terrified when I heard the news." (Voice drops slightly, pace slows, you can hear the fear)

**You don't have to perform emotion theatrically. Just don't flatten it.**

**Practice:**

Tell a 30-second story three ways:
1. Completely flat (monotone, no tonal variation)
2. Exaggerated (overly dramatic, performing every emotion)
3. Natural but present (letting tone reflect genuine feeling without performing)

Notice: Which one feels authentic? Which one would you want to hear?

Most people err on the side of flattening. Give yourself permission for tonal range.

---

**2. PACING (Speed and Rhythm)**

Pacing guides attention. Fast pacing creates energy. Slow pacing creates weight.

**Strategic pacing in storytelling:**

**FAST pacing for:**
- Action sequences ("I sprinted down the hall, grabbed my keys, and flew out the door")
- Building tension ("And then this happened, and then that happened, and then...")
- Moments of chaos or urgency

**SLOW pacing for:**
- Emotional weight ("I stood there... holding his watch... trying to make sense of it")
- Important realisations ("That's when I understood...")
- Moments you want listeners to feel deeply

**VARIED pacing for:**
- Natural, engaging storytelling (not one speed throughout)

**Common pacing mistake: Rushing through emotional peaks**

When you're nervous or uncomfortable with vulnerability, you rush. The most important part of the story gets compressed into a blur.

**Example:**

❌ **Rushed:** "So my dad died and it was really hard and I cried a lot and eventually I processed it and now I'm okay." (Said in 5 seconds)

✅ **Slowed for emotional peak:** "My dad died. [pause] The funeral was three days later. I remember standing in front of his casket, and I couldn't cry. I just felt... numb. It wasn't until two weeks later, alone in my kitchen, that I finally broke down."

The second version gives the emotional moment space to breathe. Listeners can feel it with you.

**Practice: Identify your default pace**

Record yourself telling a 2-minute story.

Listen back:
- Do you rush through the whole thing?
- Do you slow down naturally at emotional moments?
- Do you vary pace or stay constant?

Most people discover they rush more than they realise, especially through vulnerable parts.

**The fix: Consciously slow down at emotional peaks. It will feel awkward at first. It won't sound awkward to listeners.**

---

**3. PAUSING (The Power of Silence)**

**Pausing is one of the most underused tools in storytelling.**

A well-placed pause:
- Gives listeners time to absorb
- Creates anticipation
- Emphasises what comes next
- Allows emotion to land

**Strategic pauses:**

**After a significant statement:**
"I realised I didn't want to be married anymore. [pause] I'd known for months, but saying it out loud made it real."

**Before a revelation:**
"I opened the email. [pause] I'd got the job."

**In place of filler words:**
Instead of "um" or "like," just pause. Silence is fine.

**During emotional moments:**
"He looked at me and said... [pause] ...'I'm proud of you.' [pause] I'd waited 30 years to hear that."

**Common mistake: Filling every silence**

Silence usually feels longer to you, the speaker, than it does to your listeners. A pause that feels awkward to you often lands as powerful.

**Practice: The strategic pause exercise**

Tell a story and intentionally pause:
- Before important statements
- After emotional moments
- Instead of filler words

It will feel strange. Record yourself. Listen back. Notice: The pauses don't sound awkward. They sound intentional.

**Master storytellers pause frequently. Nervous storytellers fill every gap.**

---

**4. VOLUME (Loud and Soft for Emphasis)**

Volume variation keeps listeners engaged.

**Most people speak at one volume throughout.** This is fine for information transfer. It's boring for storytelling.

**Strategic volume:**

**Louder for:**
- Emphasis ("I was NOT going to let that happen")
- Climactic moments ("And then I said YES")
- Energy and intensity

**Softer for:**
- Intimacy ("I whispered, 'I'm scared'")
- Vulnerability ("I don't tell many people this, but...")
- Drawing listeners in (they have to lean in to hear)

**The counterintuitive move: Getting quieter at emotional peaks**

**Instead of:** Getting louder when emotion is high (which can feel performative)

**Try:** Getting quieter, slower, more intimate

"I stood there, holding my father's watch. [quieter] It was still warm."

Softness creates intimacy. Listeners lean in. You've created a moment of shared closeness.

**Practice: Volume range**

Tell a 1-minute story and intentionally vary volume:
- Start at your normal speaking volume
- Go louder for one moment of emphasis
- Go quieter for one vulnerable moment
- Return to normal

Notice: It feels dramatic to you. It sounds engaging to listeners.

---

**Putting It Together: Voice as Instrument**

**Imagine your voice has four dials:**
- **Tone** (emotional colour)
- **Pacing** (speed and rhythm)
- **Pausing** (strategic silence)
- **Volume** (loud and soft)

**Most people leave all four dials at the default setting throughout.** The story becomes a monotonous hum.

**Storytellers adjust the dials intentionally:**
- Slow down for emotional weight
- Pause before revelations
- Vary volume for emphasis
- Let tone reflect genuine feeling

**You don't need to be a voice actor. You just need to access more of your natural range.**`,

          practicalApplication: `**Voice Dynamics Practice**

Choose a 2-minute story.

**Round 1: Record with no variation**
- Flat tone, constant pace, no pauses, same volume throughout
- Listen back. Notice: It's boring, even if the content is good.

**Round 2: Record with intentional variation**
Use the four dials:
- **Tone:** Let your voice reflect emotion (don't flatten)
- **Pacing:** Slow down at emotional peaks, speed up for action/tension
- **Pausing:** Pause before important statements, after emotional moments
- **Volume:** Go louder for emphasis, quieter for vulnerability

Listen back. Notice the difference.

**Round 3: Tell it to someone**
Use the variation you practised. Watch their face. Do they lean in? Do they stay engaged?

**The goal: Make variation feel natural, not performed.**

Over time, this becomes instinctive. But initially, it helps to practise consciously.`,
        },
      },

      {
        id: 'body-language',
        title: 'Body Language: Stance, Gestures, Eye Contact',
        duration: '8 minutes',
        content: {
          intro: `Picture a woman telling you a powerful story about standing up to her boss, while her body does the exact opposite of standing up.

Arms crossed tightly. Shoulders hunched forward. Eyes looking down. Shifting weight from foot to foot like she wants to escape.

The words say "I found my courage." The body says "I'm still terrified and ashamed."

When she finishes, you gently say: "That took courage, both then and now. But can I show you something?"

You ask her to tell the last 30 seconds again, just the part where she actually spoke to her boss. But this time: "Stand tall. Shoulders back. Look at me. Let your hands be open, not crossed."

She does. And her whole energy shifts. The story lands completely differently. She can feel it herself; her voice even changes without you saying anything about voice.

"That's body language," you say. "Your body is telling a story alongside your words. When they match, the story amplifies. When they don't, people tend to believe your body over your words about how you feel."

Let's make sure they're telling the same story.`,

          mainContent: `**The Three Elements of Body Language**

**1. STANCE (How You Hold Your Body)**

Your stance signals confidence, openness, or discomfort before you say a word.

**Closed stance (signals discomfort, defensiveness):**
- Crossed arms
- Hunched shoulders
- Weight shifted to one foot (looks like you're ready to leave)
- Hands in pockets
- Body angled away from listener

**Open stance (signals confidence, availability):**
- Arms at sides or gesturing
- Shoulders back, chest open
- Weight evenly distributed
- Body facing listener
- Grounded (not swaying or shifting)

**You don't need to force a "power pose" whilst storytelling.** But notice: Is your body open or closed?

**If you're telling a vulnerable story whilst crossed-armed and hunched, you're sending mixed signals.**
- Your words say: "I trust you with this"
- Your body says: "I'm protecting myself from you"

**Practice: Open stance storytelling**

Tell a story whilst consciously adopting an open stance:
- Arms uncrossed (let them hang or gesture naturally)
- Shoulders relaxed, chest open
- Feet grounded, weight evenly distributed
- Body facing the listener

It will feel vulnerable (because you're physically opening yourself). That's the point.

**Notice: Does the story land differently when your body is open?**

---

**2. GESTURES (Hand and Arm Movement)**

Gestures emphasise, illustrate, and add energy.

**Three types of gestures:**

**Illustrative gestures** (show what you're describing):
- "The room was THIS big" [hands wide apart]
- "She was tiny" [hand low to ground]
- "I was running" [pumping arms]

**Emphatic gestures** (emphasise points):
- "I was NOT going to do it" [hand cutting through air]
- "This matters" [hand on heart]
- "Listen to this" [pointing]

**Nervous gestures** (distract from content):
- Fidgeting with hair, jewellery, clothing
- Wringing hands
- Adjusting glasses repeatedly
- Touching face

**The guideline: Gesture when it serves the story. Still your hands when it doesn't.**

**Common mistakes:**

**Too few gestures (stiff, robotic):**
Hands locked at sides or gripping podium. Looks controlled and distant.

**Too many gestures (distracting):**
Constant movement, every sentence punctuated with hand flourishes. Listener watches hands instead of connecting with story.

**Nervous gestures (undermines message):**
Fidgeting whilst saying "I'm confident about this decision" → Body contradicts words

**The sweet spot: Natural gestures that support content, stillness when content can stand alone.**

**Practice: Gesture awareness**

Record yourself telling a story.

Watch with sound OFF. Just watch your hands.
- Are you gesturing naturally?
- Are you fidgeting?
- Are you completely still (which can look stiff)?
- Do your gestures support the story or distract from it?

Then try again, consciously:
- Use illustrative gestures when describing something visual
- Use emphatic gestures when making an important point
- Rest your hands calmly when you're not gesturing (don't fidget)

**The goal: Gestures that feel natural and support meaning, not performance or nervousness.**

---

**3. EYE CONTACT (Connection and Trust)**

In many settings, eye contact is where connection happens.

**In one-on-one conversation:**
- Look at your listener for a good share of the time, with natural breaks (constant eye contact can feel intense). How much feels right varies between people and cultures, so take your cue from your listener.
- Look away briefly when thinking or transitioning
- Return eye contact during emotional moments, if that suits your listener (this is often where connection lives)

**Too little eye contact:**
- Looking at floor, ceiling, or distance whilst talking
- In many settings it can signal discomfort or disconnection (in some cultures, less eye contact is a sign of respect)
- Listener may feel shut out

**Too much eye contact:**
- Unblinking staring
- Can feel aggressive or intense
- Listener feels uncomfortable

**The sweet spot: Natural eye contact with occasional breaks.**

**In group settings:**
- Scan the room, making brief eye contact with different people
- Don't fixate on one person
- Don't look over heads or at back wall (disconnects from everyone)

**During vulnerable moments:**

This is counterintuitive: **when you're sharing something vulnerable, try not to hide from your listener.**

**Why it's hard:** Vulnerability triggers a desire to look away (protect yourself)

**Why it matters:** In many English-speaking settings, looking away at a vulnerable moment can read as shame, and gentle eye contact as trust. That isn't universal: in some cultures, including for many Aboriginal and Torres Strait Islander and Māori people, looking away is a sign of respect. Follow your listener's lead.

**Example:**

❌ "I've been struggling with depression." [looking at floor]
→ Listener may think: They're ashamed, I should be careful

✅ "I've been struggling with depression." [gentle eye contact]
→ Listener may think: They trust me with this, I can hold it

If you might be in danger or thinking about suicide, get help now: ${CRISIS_HELP}.

**The practice: Where it suits your setting, keep soft eye contact during vulnerable shares (not staring, not hiding).**

[INSIGHT: The Vulnerability-Eye Contact Paradox | In many settings people look away when they feel ashamed or uncomfortable. When you share something vulnerable on purpose (Module 6), gentle eye contact can show you're at ease with what you're sharing and that you trust your listener. Adjust to your listener's culture and comfort, and practise in low-stakes conversations first, then apply it to higher-stakes vulnerable shares.]

**Practice: Eye contact calibration**

In your next three conversations, notice:
- What percentage of the time do you make eye contact?
- Do you look away during vulnerable moments?
- Does the other person feel connected to or shut out by your eye contact patterns?

Then practise:
- Increasing eye contact slightly (if you tend to avoid)
- Breaking eye contact occasionally (if you tend to stare)
- Keeping gentle eye contact during vulnerable moments, if it suits your setting (even when instinct says look away)

**The goal: Eye contact that creates connection without intensity.**`,

          practicalApplication: `**Body Language Awareness Exercise**

Record yourself telling a 3-minute story (video, not just audio).

**Watch it THREE times:**

**Watch 1: Sound OFF, watch your body**
- Stance: Open or closed? Grounded or swaying?
- Gestures: Natural, stiff, or fidgety?
- Overall: Does your body look engaged or defended?

**Watch 2: Sound OFF, watch your face and eyes**
- Eye contact: Do you look at the camera/person or away?
- Facial expressions: Do they match the emotional content?
- Do you look present or disconnected?

**Watch 3: Sound ON, watch everything together**
- Do voice, body, and words align?
- Where do they mismatch?
- What would you adjust?

**Then practise ONE adjustment:**
- If your stance is closed → practise open stance
- If you fidget → practise stillness
- If you avoid eye contact → practise gentle eye contact, if it suits your setting
- If you're stiff → practise natural gestures

Record again with that one adjustment. Notice the difference.

You're not trying to be perfect. You're removing barriers to presence.`,
        },
      },

      {
        id: 'nervous-habits',
        title: 'Managing Nervous Habits (With Compassion)',
        duration: '7 minutes',
        content: {
          intro: `Picture counting someone's "ums" during a presentation. Not to be mean, just out of curiosity. Say she says "um" 47 times in 10 minutes. That's about once every 13 seconds.

Afterwards, you mention it gently. She's horrified. "I had no idea. Do I really do that?"

"Yeah. But I don't think you're broken. I think you're nervous and trying to fill every silence because silence feels scary."

She nods. "I hate pauses. I feel like I'm losing people's attention."

"I know. But the opposite is true. Every time you say 'um,' I focus on the 'um' instead of what you're saying. When you pause, I focus on the last thing you said. Pauses are powerful. Silence is okay."

Let's talk about the elephant in the room: nervous habits.

Filler words. Fidgeting. Rushing. Apologising before you start. "Does that make sense?" after every sentence.

**Everyone has nervous habits. You're not broken.**

This section isn't about shame. It's about **noticing patterns with compassion and reducing habits that undermine your message**.`,

          mainContent: `**The Most Common Nervous Habits in Storytelling**

**1. Filler words ("um," "like," "you know," "so," "basically")**

**Why we do it:** Fills silence whilst our brain catches up. Signals we're still talking (don't interrupt). Psycholinguists Herbert Clark and Jean Fox Tree (2002) found speakers use "uh" before a short delay and "um" before a longer one.

**Why it matters:** Occasional filler words are fine. Constant filler words distract from content.

**The fix:**
- **Pause instead of filling.** When you feel "um" coming, just... pause. Silence is okay.
- **Record yourself.** Count filler words. You probably use more than you realise.
- **Practise.** It gets better with repetition.

**Self-compassion note:** Don't aim for zero filler words. Aim for fewer. Perfection sounds robotic. A few "ums" sound human.

---

**2. Rushing (speaking too fast, especially through emotional content)**

**Why we do it:** Nervousness speeds us up. Discomfort makes us want to get through it quickly.

**Why it matters:** Rushing through emotional peaks robs them of impact. Listeners can't absorb what you're saying.

**The fix:**
- **Notice when you rush.** Usually it's during vulnerability or when you're nervous.
- **Consciously slow down.** It will feel uncomfortably slow to you. In our experience, it usually sounds fine to listeners.
- **Pause.** Especially before and after important moments.

---

**3. Apologising or self-deprecating before you start**

**Examples:**
- "Sorry, this might be boring..."
- "I'm not very good at this, but..."
- "This is probably stupid, but..."
- "I'll keep it short..." [proceeds to tell a long story apologetically]

**Why we do it:** Preemptive self-protection. If I say it's bad first, you can't judge me for it.

**Why it matters:** You're undermining your own story before you begin. You're telling listeners "Don't take this seriously."

**The fix:**
- **Just start.** Don't apologise. Don't disclaim. Trust that your story is worth telling.
- **If you catch yourself disclaiming, stop and restart:** "Actually, never mind that. What I want to share is this..."

---

**4. Seeking reassurance mid-story**

**Examples:**
- "Does that make sense?"
- "Am I making sense?"
- "You know what I mean?"
- "Is this boring?"

**Why we do it:** Checking if they're still with us. Seeking validation.

**Why it matters:** It interrupts narrative flow. It puts emotional labour on the listener to reassure you.

**The fix:**
- **Watch their face instead.** Are they engaged? Then keep going. You don't need to ask.
- **Ask at the end, not during:** "Did that make sense?" is fine after the story. During the story, it disrupts.

---

**5. Fidgeting (hands, jewellery, hair, clothing)**

**Why we do it:** Nervous energy needs an outlet.

**Why it matters:** Distracts listeners. Signals discomfort.

**The fix:**
- **Notice your default fidget.** (Twirling hair? Adjusting glasses? Picking at nails?)
- **Occupy your hands intentionally.** Hold a pen. Rest hands on table. Gesture naturally.
- **If standing, ground yourself.** Feet hip-width apart. Let arms hang or gesture. Don't sway.

---

**The Self-Compassion Principle**

**What NOT to do:** Shame yourself for nervous habits.

"I'm so bad at this."
"I can't stop saying 'um.' I'm the worst."
"Everyone can tell I'm nervous. I'm failing."

**Shame makes nervous habits worse.** When you're anxious about being anxious, you become more anxious.

[INSIGHT: The Anxiety-Performance Loop | Many nervous speakers get caught in a loop: you're nervous → you notice you're nervous → you judge yourself for being nervous → you become more nervous. Breaking the loop takes self-compassion: "I'm nervous. That's human. It doesn't mean I'm failing." Studies using Kristin Neff's Self-Compassion Scale link self-kindness with lower anxiety (a 2012 meta-analysis by MacBeth and Gumley found a large association, though the studies were correlational), and in experiments by Breines and Chen (2012), people who met a failure with self-compassion were more motivated to improve and studied longer afterwards, even though their test scores didn't change significantly. Applied to storytelling: notice nervous habits without shame. "I said 'um' a lot. That's okay. I'll practise pausing instead." In our experience, this takes the edge off and makes habits easier to change.]

**What TO do instead: Notice with curiosity, not judgement.**

"Interesting. I rushed through that emotional bit. I'll try slowing down next time."

"I said 'um' probably 20 times. Let me practise pausing instead."

"I apologised before I started. I'm going to try starting directly next time."

**Curiosity and practice reduce nervous habits. Shame amplifies them.**

---

**The Practice Loop**

Changing nervous habits takes practice. Use this loop:

**1. Record yourself** (audio or video)

**2. Watch/listen without judgement**
- Just notice patterns
- "I fidget with my ring when I'm talking about my father."
- "I rush through vulnerable parts."
- "I say 'like' every third word."

**3. Choose ONE habit to work on** (not all of them at once)

**4. Practise intentionally**
- Tell stories where you consciously work on that one habit
- Example: "This week, I'm going to pause instead of saying 'um.'"

**5. Record again**
- Notice improvement
- Celebrate progress (not perfection)

**6. Move to the next habit**

**In our experience, most nervous habits ease noticeably after a few rounds of this loop.** You're not aiming for zero. You're aiming for "not distracting."

---

**The Vulnerability Paradox**

**A little nervousness can actually humanise you.**

If you're delivering a vulnerable story with polished perfection (no filler words, perfect pacing, flawless delivery), it can feel performed.

If you're delivering the same story with slight nervousness showing (a few "ums," a moment where your voice catches), it signals genuineness.

**The difference between distracting nervousness and humanising nervousness:**

**Distracting:** Constant filler words, fidgeting, rushing, apologising
→ Listeners focus on your discomfort instead of your story

**Humanising:** A few hesitations, genuine emotion showing through
→ Listeners connect with your authenticity

**You don't need to be perfect. You need to be present.**`,

          practicalApplication: `**Nervous Habit Inventory**

**Step 1: Record yourself telling a 3-minute story**

**Step 2: Watch/listen and tally your nervous habits:**
- Filler words ("um," "like," etc.): ___ times
- Apologising/disclaiming: Yes / No
- Rushing (especially through emotional content): Yes / No
- Seeking reassurance mid-story: ___ times
- Fidgeting: What did you fidget with?
- Other patterns you notice:

**Step 3: Notice WITHOUT JUDGEMENT**
- Don't shame yourself
- Just observe: "Interesting. I do that."

**Step 4: Choose ONE habit to work on this week**
- Not all of them. Just one.
- Example: "I'm going to practise pausing instead of saying 'um.'"

**Step 5: Practice week**
- Tell stories this week where you consciously work on that habit
- Notice improvement
- Be kind to yourself when you slip

**Step 6: Record again in a week or two**
- Count again
- Notice progress (it won't be perfect, and that's okay)

**The goal: Reduction, not elimination. Progress, not perfection.**

Remember: You're learning a skill. Skills improve with practice and self-compassion, not shame.`,
        },
      },

      {
        id: 'rehearsal-presence',
        title: 'Rehearsal Without Becoming Robotic',
        duration: '6 minutes',
        content: {
          intro: `Picture a student who memorises a story word for word for a presentation. She practises it fifty times. Knows every sentence perfectly.

Then she delivers it in class. And it's... awful. Lifeless. Like watching someone recite a script they didn't understand. She never looks up. Never pauses naturally. Just powers through to the end like she's trying to finish before she forgets the next line.

"That felt terrible," she says afterwards. "I knew exactly what I was going to say, but it felt fake. Like I wasn't even there."

"You weren't," her teacher says. "You were too busy remembering the exact words to actually be present with the story."

"So should I not rehearse?"

"No, you should rehearse. Just not like that."

You've learnt voice dynamics, body language, and how to manage nervous habits.

Now the crucial question: **How do you practise without losing spontaneity?**

Because there's a balance. Too little rehearsal → you're unprepared, stumbling, anxious. Too much rehearsal → you sound scripted, robotic, disconnected.

Let's find the sweet spot where you're prepared enough to be confident but loose enough to be present.`,

          mainContent: `**The Rehearsal Paradox**

**No rehearsal:** You forget important parts. You ramble. You get lost mid-story.

**Over-rehearsal:** You memorise exact words. You sound like you're reciting. You've lost presence.

**The sweet spot: Rehearse the structure and key moments, but allow flexibility in the telling.**

---

**What to Rehearse**

**1. Story structure (the bones)**

Know your five-part structure (Module 4):
- Setup: What's the before state?
- Catalyst: What disrupted it?
- Tension: What obstacles/attempts/struggles?
- Climax: What's the peak moment?
- Resolution: What's the after state/learning?

**Rehearse: The arc.** Where does the story go?

**Don't rehearse: The exact words.** Let those emerge naturally.

---

**2. Key moments (the anchors)**

Identify 3-5 key moments in your story, the bits that need to land perfectly:

- The opening line (hooks attention)
- The emotional peak (where feeling lives)
- The realisation/shift (climax)
- The resolution/meaning (landing)
- Any specific sensory details (Module 5) that create resonance

**Rehearse: These specific moments.** Get the wording tight.

**Don't rehearse: All the connective tissue between them.** That can be looser.

---

**3. Delivery choices (the craft)**

Based on this module, rehearse:
- Where will I slow down? (Emotional peaks)
- Where will I pause? (Before revelations, after important moments)
- Where will I vary volume? (Louder for emphasis, quieter for intimacy)
- What's my stance? (Open, grounded)

**Rehearse: The intentional choices.**

**Don't rehearse: Every single gesture or facial expression.** That becomes performative.

---

**How to Rehearse**

**Option 1: Out loud to yourself (3-5 times)**

- Tell the story out loud
- Don't read from notes
- Focus on structure and key moments
- Notice where you stumble (those bits need more clarity)
- Notice what feels natural (keep that)

**Option 2: Record yourself (audio or video)**

- Tell the story
- Listen/watch back
- Notice:
  - Did I hit the key moments clearly?
  - Did I use voice dynamics effectively?
  - Did my body support or undermine the story?
- Adjust and try again

**Option 3: Tell it to a safe person**

- Someone who'll give honest feedback
- Tell the story as you'd tell it in the real context
- Ask: "Where did you engage? Where did you drift?"
- Refine based on feedback

**How many times to rehearse:**

**Low-stakes contexts (casual conversation, dinner with friends):** 0-1 times
- You don't need to rehearse casual stories
- Trust your natural storytelling

**Medium-stakes (job interview, first date, presentation):** 3-5 times
- Enough to know your structure and key moments
- Not so much that you sound scripted

**High-stakes (big presentation, important conversation, public talk):** 5-10 times
- Rehearse until the structure is automatic
- But still leave room for in-the-moment responsiveness

---

**The Flexibility Principle**

Even after rehearsal, **be willing to adapt in the moment.**

If you're telling a story and you notice:
- The listener isn't engaged (yellow/red lights from Module 4) → compress or pivot
- They're deeply engaged (green lights) → you can expand emotional moments
- The context shifts (more formal than expected) → adjust vulnerability level (Module 6)

**Rehearsal gives you confidence in the structure. Presence allows you to respond to what's actually happening.**

[INSIGHT: The Jazz Metaphor | Jazz musicians rehearse scales, chord progressions, and song structures extensively. But in performance, they improvise, responding to the moment, the other musicians, the audience. Storytelling is similar. Rehearse the structure and key moments (your "scales and chords"). But in the actual telling, respond to the listener, the context, the energy of the moment. That's presence. Robotic delivery happens when you're so attached to what you rehearsed that you ignore what's actually happening. Confident presence happens when rehearsal gives you a foundation to improvise from.]

---

**The Presence Check**

After rehearsal, ask yourself:

**Am I:**
- ✅ Prepared (I know my structure and key moments)
- ✅ Flexible (I can adapt if the context shifts)
- ✅ Present (I'm connected to the story's genuine meaning, not just performing words)

**Or am I:**
- ❌ Memorising exact words (sounds scripted)
- ❌ Attached to one version (can't adapt to the moment)
- ❌ Performing rather than sharing (lost connection to genuine feeling)

**If you're in the second category, pull back on rehearsal. You've over-prepared.**

---

**The Ultimate Presence Practice: Tell It Around 20 Times**

If you have a story you'll tell repeatedly (your "signature stories" from Module 1), try this:

**Our rule of thumb: tell it around 20 times, to different people, in real contexts.**

Well before the end, the structure usually becomes automatic. You're no longer thinking about what comes next.

By the end, you're free to respond to each listener, and the story feels alive, not fixed.

**This is mastery: The story is so integrated that you can be fully present with each unique telling.**

---

**Before High-Stakes Storytelling**

Right before an important storytelling moment (interview, presentation, difficult conversation):

**30 minutes before:**
- Review key moments (don't rehearse the whole thing)
- Stand or sit tall and open for two minutes (see Presence preparation in the first section of this module)
- Take a few slow breaths with long, gentle out-breaths (a 2018 review by Zaccaro and colleagues linked slow breathing with feeling calmer and less aroused)

**5 minutes before:**
- Don't rehearse (you'll psych yourself out)
- Ground yourself (feel your feet, breathe)
- Remind yourself: "I know this story. I've prepared. Now I'm going to be present."

**During:**
- Trust your preparation
- Be with the listener
- Let the story emerge naturally

**That's the balance: Prepared enough to be confident. Present enough to be real.**`,

          practicalApplication: `**Rehearsal Practice: The 3-Round Method**

Choose one story you'll tell in a medium or high-stakes context soon.

**Round 1: Structure rehearsal (out loud)**
- Tell the story focussing only on structure
- Don't worry about perfect wording
- Just make sure you hit: Setup → Catalyst → Tension → Climax → Resolution
- Notice where you get lost or ramble (tighten those sections)

**Round 2: Key moments rehearsal (record it)**
- Identify your 3-5 key moments (opening, emotional peak, realisation, resolution)
- Get the wording clear for those moments
- Let everything else be flexible
- Listen back: Did the key moments land clearly?

**Round 3: Delivery rehearsal (full presence)**
- Tell it as you would in the real context
- Apply voice dynamics (tone, pacing, pausing, volume)
- Use open body language
- Practise gentle eye contact, if it suits your setting (even if you're alone, imagine the listener)
- Don't aim for perfection; aim for presence

**Then: Trust it**
- You've rehearsed enough
- In the real moment, be present
- Respond to what's actually happening

**After: Reflect**
- How did it go?
- Where did preparation help?
- Where did you need to adapt in the moment?
- What would you adjust for next time?

This 3-round method takes 30-45 minutes total. It's enough to build confidence without becoming robotic.`,
        },
      },
    ],

    exercises: [
      {
        id: 'voice-dynamics-recording',
        title: 'Voice Dynamics Recording Practice',
        type: 'Practice',
        duration: '30 minutes',
        description: 'Record yourself telling a story three ways to build awareness of tone, pacing, pausing, and volume.',
        instructions: [
          'Choose a 2-3 minute story you tell regularly',
          '**Recording 1: Flat delivery (no variation)**',
          '  - Monotone, constant pace, no pauses, same volume',
          '  - Listen back: Notice how boring it sounds despite good content',
          '**Recording 2: Exaggerated variation (over-performed)**',
          '  - Dramatic tone shifts, theatrical pacing, long pauses, big volume changes',
          '  - Listen back: Notice it feels fake/performed',
          '**Recording 3: Natural variation (the sweet spot)**',
          '  - Tone reflects genuine emotion (not flattened)',
          '  - Pace slows at emotional peaks, speeds up for action',
          '  - Pauses before revelations, after emotional moments',
          '  - Volume varies (louder for emphasis, quieter for intimacy)',
          '  - Listen back: Does this sound authentic AND engaging?',
          'Compare all three recordings',
          'Identify which elements from Recording 3 you want to incorporate naturally',
          'Practice run: tell the story to a real person using natural variation',
        ],
      },
      {
        id: 'body-language-video-audit',
        title: 'Body Language Video Audit',
        type: 'Reflection',
        duration: '40 minutes',
        description: 'Record and analyse your body language to identify patterns and one key adjustment.',
        instructions: [
          'Record yourself (VIDEO) telling a 3-minute story',
          '**Watch 1: Sound OFF (focus on body)**',
          '  - Stance: Open or closed? Grounded or swaying?',
          '  - Gestures: Natural, stiff, or fidgety? Do they support content?',
          '  - Overall impression: Engaged or defended?',
          '**Watch 2: Sound OFF (focus on face/eyes)**',
          '  - Eye contact: Looking at camera/person or away? How often?',
          '  - Facial expressions: Do they match emotional content?',
          '  - Presence: Does your face look engaged with the story, or somewhere else?',
          '**Watch 3: Sound ON (watch integration)**',
          '  - Do voice, body, and words align?',
          '  - Where do they mismatch?',
          '  - What undermines your message?',
          'Identify ONE adjustment to practise:',
          '  - If closed stance → practise open stance',
          '  - If fidgeting → practise stillness between gestures',
          '  - If avoiding eye contact → practise gentle eye contact, if it suits your setting',
          '  - If stiff → practise natural illustrative gestures',
          'Record again with that ONE adjustment',
          'Notice: Does the story land differently? Does your body support or distract?',
        ],
      },
      {
        id: 'nervous-habit-reduction',
        title: 'Nervous Habit Reduction (With Self-Compassion)',
        type: 'Ongoing Practice',
        duration: '15 minutes weekly for 4 weeks',
        description: 'Identify and reduce one nervous habit at a time using practice and self-compassion.',
        instructions: [
          '**Week 1: Baseline recording**',
          '  - Record yourself telling a 3-minute story',
          '  - Tally nervous habits:',
          '    • Filler words (um, like, you know): ___ count',
          '    • Rushing through emotional content: Yes/No',
          '    • Apologising or disclaiming: Yes/No',
          '    • Seeking reassurance mid-story: ___ count',
          '    • Fidgeting: What specifically?',
          '  - Notice WITHOUT judgement (just observe)',
          '**Week 2: Choose ONE habit, practise the alternative**',
          '  - Pick one habit to work on',
          '  - Example: "I\'m going to pause instead of saying \'um\'"',
          '  - Tell 3-5 stories this week consciously practising the alternative',
          '  - Be kind to yourself when you slip',
          '**Week 3: Record again, measure progress**',
          '  - Record same story',
          '  - Tally the ONE habit you\'re working on',
          '  - Notice improvement (not perfection)',
          '  - Celebrate progress',
          '**Week 4: Integrate and move to next habit**',
          '  - The first habit should be reducing naturally',
          '  - Choose the NEXT habit to work on',
          '  - Repeat the practice loop',
          'Remember: self-compassion is linked to lower anxiety. Shame tends to make nervous habits worse.',
          'Aim for reduction, not elimination. Progress, not perfection.',
        ],
      },
    ],

    keyTakeaways: [
      'Albert Mehrabian\'s 1967 studies found that when a speaker\'s words clash with their tone or facial expression about feelings or attitudes, listeners go mainly by tone and face (his 7-38-55 figures apply only to that situation), so let your voice, body and words tell the same story',
      'Amy Cuddy\'s work on presence suggests that how you hold your body can affect how you feel as well as how others see you: in replications, an open posture gives a small boost to felt power, while slumping tends to make people feel worse (the widely quoted cortisol and testosterone effects did not replicate)',
      'Voice dynamics (tone, pacing, pausing, volume) guide listener attention and create emotional impact: slow down at emotional peaks, pause before revelations, vary volume for emphasis and intimacy',
      'Open body language (uncrossed arms, shoulders back, grounded stance, eye contact that suits your listener) signals trust and availability, whilst closed posture sends mixed messages that undermine vulnerable content',
      'Nervous habits (filler words, rushing, fidgeting, apologising) reduce with practice and self-compassion, not shame; notice patterns with curiosity, choose one habit at a time, and aim for reduction (not perfection)',
    ],

    reflectionQuestions: [
      'When you tell important stories, does your body language tend to close off (crossed arms, looking away) or open up (facing your listener, eye contact that suits them)? What would it feel like to consciously adopt open body language during vulnerable shares?',
      'Do you rush through emotional peaks in your stories, or do you slow down and let them breathe? What makes you uncomfortable about lingering in emotional moments?',
      'What\'s your most distracting nervous habit (filler words, fidgeting, apologising before you start)? Can you notice it with curiosity rather than shame?',
      'If you recorded yourself telling a story, would your voice have tonal range and pauses, or would it be relatively flat and constant? What would it sound like to access more of your natural vocal range?',
      'Do you over-rehearse stories (becoming robotic) or under-rehearse (feeling unprepared)? What would the sweet spot look like for you: knowing structure and key moments whilst staying present and flexible?',
    ],

    nextSteps: `You've mastered presence: aligning your voice, body, and words to tell stories with full engagement.

**Module 8: The Art of Story Listening** completes the curriculum by teaching you how to elicit and receive others' stories. You'll learn to create space for vulnerability, ask questions that invite depth, and build on others' narratives, completing the reciprocal dance of storytelling.

**Toolkit Suggestion:** Use the **Story Rehearsal Recorder** to record yourself, self-assess your voice (filler words, pacing, pauses) and compare takes.`,
  },
};

export default storyModule07Content;
